import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

/**
 * Small on-brand confirm dialog built on top of the shadcn AlertDialog primitive.
 * Works well on mobile (Radix locks the body scroll and centers the panel).
 *
 * <ConfirmDialog
 *   open={isOpen}
 *   onOpenChange={setOpen}
 *   title="Delete client?"
 *   description="Their session history will be preserved."
 *   confirmText="Delete"
 *   destructive
 *   onConfirm={handleDelete}
 * />
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title = 'Are you sure?',
  description = null,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  destructive = false,
  onConfirm,
  confirmTestId,
  cancelTestId,
  icon: Icon = AlertTriangle,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md rounded-2xl border border-white/10 bg-[#141414] text-white">
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className={`inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-xl ${destructive ? 'bg-red-500/15 text-red-300' : 'bg-[var(--pg-admin-accent-soft)] text-[var(--pg-admin-accent)]'}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <AlertDialogTitle className="font-display text-2xl leading-tight">{title}</AlertDialogTitle>
              {description && (
                <AlertDialogDescription className="mt-1 text-sm text-white/70">
                  {description}
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            data-testid={cancelTestId}
            className="rounded-xl border-white/15 bg-transparent text-white hover:bg-white/5"
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={confirmTestId}
            onClick={onConfirm}
            className={
              destructive
                ? 'rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold'
                : 'rounded-xl bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] font-semibold'
            }
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
